import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { ArrowLeft, MapPin, Phone, Globe, Compass, User, Pencil, Trash2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { useSiteBranding } from "../contexts/SiteBrandingContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdDetail = ({ user, isAuthenticated, onLogout }) => {
  const { adId } = useParams();
  const navigate = useNavigate();
  const { branding } = useSiteBranding();
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAd();
  }, [adId]);

  const fetchAd = async () => {
    try {
      const response = await axios.get(`${API}/ads/${adId}`);
      setAd(response.data);
    } catch (error) {
      console.error("Failed to fetch ad:", error);
      toast.error("Failed to load ad details");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/ads/${adId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Ad deleted successfully!");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete ad");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-200 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!ad) {
    return (
      <div className="min-h-screen bg-gray-200 flex items-center justify-center">
        <div className="text-xl text-gray-600">Ad not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Header */}
      <header className="bg-white border-b-2 border-gray-200 sticky top-0 z-50 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 sm:py-0 sm:h-24">
            <div className="flex items-center justify-between sm:justify-start">
              <div className="flex items-center space-x-3 sm:space-x-4">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg transform hover:scale-105 transition-transform duration-300" style={{ backgroundColor: branding.logo_bg_color || '#000000' }}>
                  {branding.logo_url ? (
                    <img 
                      src={`${BACKEND_URL}${branding.logo_url}`} 
                      alt="Logo" 
                      className="w-full h-full object-contain rounded-2xl"
                    />
                  ) : (
                    <span className="text-white text-2xl sm:text-3xl font-bold">ESMC</span>
                  )}
                </div>
                <div className="flex flex-col">
                  <h1 className="text-2xl sm:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-black to-gray-700 truncate">{branding.site_title}</h1>
                  {isAuthenticated && (
                    <span className="text-gray-600 font-medium text-xs sm:hidden mt-1">Welcome, {user?.name}</span>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-2 sm:hidden ml-2">
                {isAuthenticated ? (
                  <Button
                    onClick={onLogout}
                    variant="outline"
                    size="icon"
                    className="h-10 w-10 border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-100 rounded-xl"
                  >
                    <LogOut className="w-5 h-5" />
                  </Button>
                ) : (
                  <Button
                    onClick={() => navigate("/login")}
                    size="icon"
                    className="h-10 w-10 bg-gradient-to-r from-black to-gray-900 text-white hover:from-gray-900 hover:to-black rounded-xl"
                  >
                    <User className="w-5 h-5" />
                  </Button>
                )}
              </div>
            </div>

            <div className="hidden sm:flex items-center space-x-2 sm:space-x-3">
              {isAuthenticated ? (
                <>
                  <span className="text-gray-700 font-medium text-sm sm:text-base">Welcome, {user?.name}</span>
                  <Button
                    onClick={onLogout}
                    variant="outline"
                    className="h-11 sm:h-12 border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-100 transition-all shadow-sm hover:shadow-md transform hover:scale-105 duration-200 rounded-xl px-4 sm:px-6 text-base font-medium"
                  >
                    Logout
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => navigate("/login")}
                  className="h-11 sm:h-12 bg-gradient-to-r from-black to-gray-900 text-white hover:from-gray-900 hover:to-black transition-all shadow-lg hover:shadow-xl transform hover:scale-105 duration-200 rounded-xl px-4 sm:px-6 text-base font-semibold"
                >
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12">
        {/* Back Button */}
        <Button
          onClick={() => navigate("/")}
          size="lg"
          className="mb-4 sm:mb-6 bg-black text-white hover:bg-gray-900 text-base sm:text-lg font-semibold px-6 py-3"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Ads
        </Button>

        {/* Ad Detail Card */}
        <div className="bg-white rounded-lg sm:rounded-xl shadow-md border border-gray-300 overflow-hidden">
          {/* Image */}
          {ad.image_url && (
            <div className="w-full">
              <img
                src={`${BACKEND_URL}${ad.image_url}`}
                alt={ad.title}
                className="w-full h-64 sm:h-96 object-cover"
                loading="eager"
                onError={(e) => {
                  // Only log in development to avoid production monitoring noise
                  if (process.env.NODE_ENV === 'development') {
                    console.warn('Image failed to load:', ad.image_url);
                  }
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Content */}
          <div className="p-4 sm:p-8">
            {/* Title and Actions */}
            <div className="flex justify-between items-start mb-6">
              <div className="flex-1">
                <h1 className="text-2xl sm:text-4xl font-bold text-black mb-2">{ad.title}</h1>
                <div className="flex flex-wrap gap-2 mt-3 sm:mt-4">
                  {ad.general_category && (
                    <span className="px-2 sm:px-3 py-1 bg-gray-100 text-gray-700 text-xs sm:text-sm rounded-full">
                      {ad.general_category}
                    </span>
                  )}
                  {ad.subcategory && ad.subcategory.length > 0 && ad.subcategory.filter(sub => sub && sub.trim()).map((sub, index) => (
                    <span key={index} className="px-2 sm:px-3 py-1 bg-gray-100 text-gray-700 text-xs sm:text-sm rounded-full">
                      {sub}
                    </span>
                  ))}
                </div>
              </div>

              {isAuthenticated && user?.email === process.env.REACT_APP_ADMIN_EMAIL && (
                <div className="flex gap-1 sm:gap-2 ml-2 sm:ml-4 flex-shrink-0">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => navigate(`/edit-ad/${ad.id}`)}
                    title="Edit ad"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleDelete}
                    title="Delete ad"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Description */}
            {ad.description && (
              <div className="mb-6 sm:mb-8">
                <h2 className="text-lg sm:text-xl font-semibold text-black mb-3">Description</h2>
                <p className="text-gray-700 text-base sm:text-lg leading-relaxed whitespace-pre-wrap">
                  {ad.description}
                </p>
              </div>
            )}

            {/* Contact Information */}
            <div className="border-t border-gray-200 pt-4 sm:pt-6">
              <h2 className="text-lg sm:text-xl font-semibold text-black mb-3 sm:mb-4">Contact Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                {ad.contact_roadname && (
                  <div className="flex items-start space-x-2 sm:space-x-3 p-3 sm:p-4 bg-gray-50 rounded-lg">
                    <User className="w-4 sm:w-5 h-4 sm:h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">Road Name</div>
                      <div className="text-sm sm:text-base text-gray-900 font-medium break-words">{ad.contact_roadname}</div>
                    </div>
                  </div>
                )}

                {ad.location && (
                  <div className="flex items-start space-x-2 sm:space-x-3 p-3 sm:p-4 bg-gray-50 rounded-lg">
                    <MapPin className="w-4 sm:w-5 h-4 sm:h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Location</div>
                      <div className="text-gray-900 font-medium">{ad.location}</div>
                    </div>
                  </div>
                )}

                {ad.service_area && ad.service_area.length > 0 && (
                  <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                    <Compass className="w-5 h-5 text-gray-600 mt-0.5" />
                    <div>
                      <div className="text-sm text-gray-500 mb-2">Service Area</div>
                      <div className="flex flex-wrap gap-2">
                        {ad.service_area.filter(area => area && area.trim()).map((area, index) => (
                          <span key={index} className="inline-block px-3 py-1 bg-white border border-gray-200 text-gray-900 text-sm rounded-full font-medium">
                            {area}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {ad.contact_phone && (
                  <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                    <Phone className="w-5 h-5 text-gray-600 mt-0.5" />
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Phone</div>
                      <a 
                        href={`tel:${ad.contact_phone}`}
                        className="text-blue-600 font-medium hover:underline"
                      >
                        {ad.contact_phone}
                      </a>
                    </div>
                  </div>
                )}

                {ad.website_link && (
                  <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                    <Globe className="w-5 h-5 text-gray-600 mt-0.5" />
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Website</div>
                      <a 
                        href={ad.website_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 font-medium hover:underline break-all"
                      >
                        {ad.website_link}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Social Media Links */}
            {(ad.discord_link || ad.instagram_link || ad.facebook_link || ad.telegram_link || ad.whatsapp_link || ad.linkedin_link) && (
              <div className="border-t border-gray-200 pt-4 sm:pt-6 mt-4 sm:mt-6">
                <h2 className="text-lg sm:text-xl font-semibold text-black mb-3 sm:mb-4">Connect on Social Media</h2>
                <div className="flex flex-wrap gap-2 sm:gap-4">
                  {ad.discord_link && (
                    <a
                      href={ad.discord_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg transition-colors text-sm sm:text-base"
                      title="Discord"
                    >
                      <svg className="w-5 sm:w-6 h-5 sm:h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.317 4.492c-1.53-.69-3.17-1.2-4.885-1.49a.075.075 0 0 0-.079.036c-.21.369-.444.85-.608 1.23a18.566 18.566 0 0 0-5.487 0 12.36 12.36 0 0 0-.617-1.23A.077.077 0 0 0 8.562 3c-1.714.29-3.354.8-4.885 1.491a.07.07 0 0 0-.032.027C.533 9.093-.32 13.555.099 17.961a.08.08 0 0 0 .031.055 20.03 20.03 0 0 0 5.993 2.98.078.078 0 0 0 .084-.026 13.83 13.83 0 0 0 1.226-1.963.074.074 0 0 0-.041-.104 13.201 13.201 0 0 1-1.872-.878.075.075 0 0 1-.008-.125c.126-.093.252-.19.372-.287a.075.075 0 0 1 .078-.01c3.927 1.764 8.18 1.764 12.061 0a.075.075 0 0 1 .079.009c.12.098.245.195.372.288a.075.075 0 0 1-.006.125c-.598.344-1.22.635-1.873.877a.075.075 0 0 0-.041.105c.36.687.772 1.341 1.225 1.962a.077.077 0 0 0 .084.028 19.963 19.963 0 0 0 6.002-2.981.076.076 0 0 0 .032-.054c.5-5.094-.838-9.52-3.549-13.442a.06.06 0 0 0-.031-.028zM8.02 15.278c-1.182 0-2.157-1.069-2.157-2.38 0-1.312.956-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.312-.956 2.38-2.157 2.38zm7.975 0c-1.183 0-2.157-1.069-2.157-2.38 0-1.312.955-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.312-.946 2.38-2.157 2.38z"/>
                      </svg>
                      <span className="font-medium">Discord</span>
                    </a>
                  )}
                  {ad.instagram_link && (
                    <a
                      href={ad.instagram_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-3 bg-pink-50 hover:bg-pink-100 text-pink-700 rounded-lg transition-colors"
                      title="Instagram"
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                      <span className="font-medium">Instagram</span>
                    </a>
                  )}
                  {ad.facebook_link && (
                    <a
                      href={ad.facebook_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-3 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors"
                      title="Facebook"
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      <span className="font-medium">Facebook</span>
                    </a>
                  )}
                  {ad.telegram_link && (
                    <a
                      href={ad.telegram_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-3 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors"
                      title="Telegram"
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                      </svg>
                      <span className="font-medium">Telegram</span>
                    </a>
                  )}
                  {ad.whatsapp_link && (
                    <a
                      href={ad.whatsapp_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-3 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg transition-colors"
                      title="WhatsApp"
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                      <span className="font-medium">WhatsApp</span>
                    </a>
                  )}
                  {ad.linkedin_link && (
                    <a
                      href={ad.linkedin_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors text-sm sm:text-base"
                      title="LinkedIn"
                    >
                      <svg className="w-5 sm:w-6 h-5 sm:h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                      <span className="font-medium">LinkedIn</span>
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdDetail;
